import React from "react";
import { AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { selectWarningCount } from "./overviewSelectors";

/**
 * Warnings, from the two endpoints that already produce them.
 *
 * `/critical-units` is high severity, `/alerts` is medium (KPI_SEMANTICS C.6 / D).
 * NO NEW ENGINE: inventing a third source would mean deciding what counts as a
 * warning in the frontend, where it cannot be tested against the data.
 *
 * The count badge is computed from the rows actually present. In production all these
 * signals are currently zero, so the empty state is the state operators will usually
 * see — it is written as a real answer ("Keine aktuellen Warnungen"), not as an
 * absence of content.
 *
 * Severity is conveyed by icon AND text, never by colour alone.
 */

export default function OverviewWarningsCard({ alerts, criticalUnits, loading, onRetry }) {
  const error = alerts?.error || criticalUnits?.error;
  const critical = Array.isArray(criticalUnits?.data?.critical_units)
    ? criticalUnits.data.critical_units
    : [];
  const medium = Array.isArray(alerts?.data?.alerts) ? alerts.data.alerts : [];
  const count = selectWarningCount(alerts?.data, criticalUnits?.data);

  return (
    <section
      aria-labelledby="overview-warnings-heading"
      aria-busy={loading || undefined}
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <h2
          id="overview-warnings-heading"
          className="text-[13px] font-semibold text-card-foreground"
        >
          Warnungen &amp; Hinweise
        </h2>
        {!loading && !error && count > 0 ? (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
            {count}
          </span>
        ) : null}
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : error ? (
          <div className="flex flex-wrap items-center gap-2">
            <p role="alert" className="text-[12px] text-destructive">
              Warnungen konnten nicht geladen werden.
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              >
                Erneut laden
              </button>
            ) : null}
          </div>
        ) : count === 0 ? (
          <p className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <CheckCircle2
              aria-hidden
              className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
            />
            Keine aktuellen Warnungen
          </p>
        ) : (
          <ul className="space-y-2">
            {critical.map((u) => (
              <li
                key={`critical-${u.unit_id}`}
                className="rounded-lg border border-border border-l-2 border-l-rose-500 bg-muted/40 p-2.5 dark:border-l-rose-400"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    aria-hidden
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600 dark:text-rose-400"
                  />
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-card-foreground">
                      {u.unit_name}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Hoch · {u.criteria_count}{" "}
                      {u.criteria_count === 1 ? "Kriterium" : "Kriterien"}
                    </p>
                  </div>
                </div>
              </li>
            ))}
            {medium.map((a) => (
              <li
                key={`alert-${a.unit_id}-${a.message}`}
                className="rounded-lg border border-border border-l-2 border-l-amber-500 bg-muted/40 p-2.5 dark:border-l-amber-400"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle
                    aria-hidden
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                  />
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-card-foreground">
                      {a.unit_name}
                    </p>
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      Mittel · {a.message}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
